import { useEffect, useRef, useState, useCallback } from "react";
import { logError } from "@/lib/logError";
import { useParams, useNavigate } from "react-router-dom";
import {
  Square, Circle, MoveUpRight, Ruler, MapPin, PenTool, Hexagon, Type, MousePointer2,
  ZoomIn, ZoomOut, Maximize, RotateCw, Home, Trash2, HelpCircle, PenLine, ArrowLeft,
  Layers, Info, Palette, Loader2, Brain, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OSDCanvas } from "@/components/wsi/OSDCanvas";
import { SignatureDialog } from "@/components/wsi/SignatureDialog";
import { AIPanel } from "@/components/wsi/AIPanel";
import OpenSeadragon from "openseadragon";

const DESKTOP_TOOLS = [
  { id: "rectangle", key: "1", icon: Square, label: "Rectangle" },
  { id: "ellipse", key: "2", icon: Circle, label: "Ellipse" },
  { id: "fleche", key: "3", icon: MoveUpRight, label: "Flèche" },
  { id: "regle", key: "4", icon: Ruler, label: "Règle (mesure)" },
  { id: "point", key: "5", icon: MapPin, label: "Point d'intérêt" },
  { id: "libre", key: "6", icon: PenTool, label: "Main levée" },
  { id: "polygone", key: "7", icon: Hexagon, label: "Polygone" },
  { id: "texte", key: "8", icon: Type, label: "Texte" },
];
const MOBILE_TOOLS = ["rectangle", "ellipse", "fleche", "point", "texte"];
const COLORS = ["#F59E0B", "#EF4444", "#14B8A6", "#3B82F6", "#22C55E"];
const CAN_ANNOTATE = ["pathologiste", "pathologiste_distant", "chef_service", "expert_externe"];
const CAN_SIGN = ["pathologiste", "pathologiste_distant", "chef_service", "expert_externe"];

const SHORTCUTS = [
  ["+ / =", "Zoom avant"], ["-", "Zoom arrière"], ["0 / Home", "Ajuster à l'écran"],
  ["Flèches", "Déplacement"], ["W A S D", "Déplacement"], ["R", "Rotation horaire"],
  ["Maj+R", "Rotation antihoraire"], ["F", "Miroir horizontal"], ["Maj+F", "Plein écran"],
  ["1", "Rectangle"], ["2", "Ellipse"], ["3", "Flèche"], ["4", "Règle"], ["5", "Point"],
  ["6", "Main levée"], ["7", "Polygone"], ["8", "Texte"], ["V", "Mode navigation"],
  ["Échap", "Annuler le tracé"], ["Suppr", "Supprimer sélection"], ["Z", "Annuler dernière annotation"],
  ["C", "Changer de couleur"], ["N", "Panneau annotations"], ["I", "Panneau informations"],
  ["G", "Afficher/masquer annotations"], ["?", "Aide raccourcis"],
];

export default function WSIViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const [manifest, setManifest] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [activeTool, setActiveTool] = useState(null);
  const [color, setColor] = useState(COLORS[0]);
  const [selectedId, setSelectedId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showAnnos, setShowAnnos] = useState(!isMobile);
  const [showInfo, setShowInfo] = useState(!isMobile);
  const [visible, setVisible] = useState(true);
  const [sigOpen, setSigOpen] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [aiRegions, setAiRegions] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const viewerRef = useRef(null);
  const sessionRef = useRef(null);

  const canAnnotate = CAN_ANNOTATE.includes(user?.role);
  const canSign = CAN_SIGN.includes(user?.role);
  const canLaunchAI = ["chef_service", "pathologiste", "pathologiste_distant", "technicien"].includes(user?.role);
  const canViewAI = canLaunchAI || ["responsable_qualite", "directeur_medical", "expert_externe"].includes(user?.role);
  const tools = isMobile ? DESKTOP_TOOLS.filter((t) => MOBILE_TOOLS.includes(t.id)) : DESKTOP_TOOLS;

  const loadManifest = useCallback(async () => {
    try {
      const { data } = await api.get(`/wsi/${id}/manifest`);
      setManifest(data);
      return data.statut;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return "erreur"; }
  }, [id]);

  const loadAnnotations = useCallback(async () => {
    const { data } = await api.get(`/wsi/${id}/annotations`);
    setAnnotations(data);
  }, [id]);

  const loadSignatures = useCallback(async () => {
    try { const { data } = await api.get(`/wsi/${id}/signatures`); setSignatures(data); } catch (e) { logError("Chargement des signatures")(e); }
  }, [id]);

  // Chargement + polling si en traitement
  useEffect(() => {
    let timer;
    (async () => {
      const st = await loadManifest();
      if (st === "en_traitement") timer = setInterval(async () => {
        const s = await loadManifest();
        if (s !== "en_traitement") clearInterval(timer);
      }, 2500);
      loadAnnotations(); loadSignatures();
    })();
    return () => timer && clearInterval(timer);
  }, [loadManifest, loadAnnotations, loadSignatures]);

  // Session de visualisation + heartbeat
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post("/wsi/sessions", { wsi_id: id, device: isMobile ? "mobile" : "desktop" });
        sessionRef.current = data.id;
      } catch (e) { logError("Session de visionnement")(e); }
    })();
    const hb = setInterval(() => {
      if (sessionRef.current) api.patch(`/wsi/sessions/${sessionRef.current}/heartbeat`).catch(logError("Pulsation de session"));
    }, 30000);
    return () => clearInterval(hb);
  }, [id, isMobile]);

  const createAnnotation = async (payload) => {
    if (!canAnnotate) { toast.error("Votre rôle ne permet pas d'annoter"); return; }
    try {
      const { data } = await api.post("/wsi/annotations", { wsi_id: id, ...payload });
      setAnnotations((prev) => [...prev, data]);
      setSelectedId(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteAnnotation = async (aid) => {
    try {
      await api.delete(`/wsi/annotations/${aid}`);
      setAnnotations((prev) => prev.filter((a) => a.id !== aid));
      if (selectedId === aid) setSelectedId(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Raccourcis clavier (bureau)
  useEffect(() => {
    if (isMobile) return;
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      const v = viewerRef.current;
      const k = e.key;
      const panBy = (dx, dy) => { if (v) { const p = v.viewport.getCenter(); v.viewport.panTo(p.plus({ x: dx, y: dy })); } };
      const map = {
        "+": () => v && v.viewport.zoomBy(1.3), "=": () => v && v.viewport.zoomBy(1.3),
        "-": () => v && v.viewport.zoomBy(0.77), "0": () => v && v.viewport.goHome(),
        Home: () => v && v.viewport.goHome(),
        ArrowUp: () => panBy(0, -0.05), ArrowDown: () => panBy(0, 0.05),
        ArrowLeft: () => panBy(-0.05, 0), ArrowRight: () => panBy(0.05, 0),
        w: () => panBy(0, -0.05), s: () => panBy(0, 0.05), a: () => panBy(-0.05, 0), d: () => panBy(0.05, 0),
        r: () => v && v.viewport.setRotation((v.viewport.getRotation() + 90) % 360),
        R: () => v && v.viewport.setRotation((v.viewport.getRotation() - 90 + 360) % 360),
        f: () => v && v.viewport.setFlip(!v.viewport.getFlip()),
        F: () => v && v.setFullScreen(!v.isFullPage()),
        v: () => setActiveTool(null), V: () => setActiveTool(null),
        Escape: () => setActiveTool(null),
        z: () => setAnnotations((p) => p.slice(0, -1)),
        Z: () => setAnnotations((p) => p.slice(0, -1)),
        c: () => setColor((c) => COLORS[(COLORS.indexOf(c) + 1) % COLORS.length]),
        C: () => setColor((c) => COLORS[(COLORS.indexOf(c) + 1) % COLORS.length]),
        n: () => setShowAnnos((s) => !s), i: () => setShowInfo((s) => !s),
        g: () => setVisible((s) => !s), "?": () => setShowHelp((s) => !s),
        Delete: () => selectedId && deleteAnnotation(selectedId),
        Backspace: () => selectedId && deleteAnnotation(selectedId),
      };
      DESKTOP_TOOLS.forEach((t) => { map[t.key] = () => canAnnotate && setActiveTool(t.id); });
      if (map[k]) { e.preventDefault(); map[k](); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, selectedId, canAnnotate]);

  const zoom = (f) => viewerRef.current && viewerRef.current.viewport.zoomBy(f);
  const home = () => viewerRef.current && viewerRef.current.viewport.goHome();
  const rotate = () => viewerRef.current && viewerRef.current.viewport.setRotation(
    (viewerRef.current.viewport.getRotation() + 90) % 360);
  const fullscreen = () => viewerRef.current && viewerRef.current.setFullScreen(!viewerRef.current.isFullPage());
  const focusRegion = (coords) => {
    const v = viewerRef.current;
    if (!v || !coords || coords.length < 2) return;
    const x = Math.min(coords[0][0], coords[1][0]);
    const y = Math.min(coords[0][1], coords[1][1]);
    const w = Math.abs(coords[1][0] - coords[0][0]) || 256;
    const h = Math.abs(coords[1][1] - coords[0][1]) || 256;
    const vr = v.viewport.imageToViewportRectangle(new OpenSeadragon.Rect(x, y, w, h));
    vr.x -= vr.width * 0.6; vr.y -= vr.height * 0.6; vr.width *= 2.2; vr.height *= 2.2;
    v.viewport.fitBounds(vr);
  };

  if (!manifest) return (
    <div className="flex items-center justify-center h-[70vh] text-slate-400" data-testid="wsi-loading">
      <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement de la lame…
    </div>
  );

  if (manifest.statut === "en_traitement") return (
    <div className="flex flex-col items-center justify-center h-[70vh] text-slate-500" data-testid="wsi-processing">
      <Loader2 className="h-8 w-8 animate-spin mb-3 text-sky-600" />
      <p className="font-medium">Génération de la pyramide de tuiles en cours…</p>
      <p className="text-xs mt-1">Moteur de repli DZI · {manifest.nom}</p>
    </div>
  );

  if (manifest.statut === "erreur") return (
    <div className="flex flex-col items-center justify-center h-[70vh] text-red-500" data-testid="wsi-error">
      <p className="font-medium">Échec du traitement de la lame</p>
      <p className="text-xs mt-1 max-w-md text-center">{manifest.erreur}</p>
    </div>
  );

  const toolBtn = (t) => {
    const Icon = t.icon;
    const active = activeTool === t.id;
    return (
      <button key={t.id} disabled={!canAnnotate}
        onClick={() => setActiveTool(active ? null : t.id)}
        title={`${t.label}${!isMobile ? ` (${t.key})` : ""}`}
        data-testid={`wsi-tool-${t.id}`}
        className={`flex items-center justify-center h-10 w-10 rounded-lg border transition-colors disabled:opacity-40 ${
          active ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
        <Icon className="h-5 w-5" />
      </button>
    );
  };

  // ===================== MOBILE =====================
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-slate-900" data-testid="wsi-mobile">
        <div className="h-12 flex items-center justify-between px-3 bg-slate-800 text-white">
          <button onClick={() => navigate("/wsi")} data-testid="wsi-mobile-back"><ArrowLeft className="h-5 w-5" /></button>
          <span className="text-xs truncate max-w-[55%]">{manifest.nom}</span>
          <button onClick={() => setColor((c) => COLORS[(COLORS.indexOf(c) + 1) % COLORS.length])}>
            <span className="h-5 w-5 rounded-full inline-block border-2 border-white" style={{ background: color }} />
          </button>
        </div>
        <div className="flex-1 relative">
          <OSDCanvas manifest={manifest} wsiId={id} mobile activeTool={activeTool} color={color}
                     annotations={visible ? annotations : []} selectedId={selectedId}
                     onSelect={setSelectedId} onCreate={createAnnotation} viewerRef={viewerRef}
                     aiRegions={aiRegions} />
          {aiOpen && (
            <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl p-3 max-h-[72%] overflow-y-auto z-20 shadow-2xl" data-testid="wsi-mobile-ai-sheet">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-sm flex items-center gap-1.5"><Brain className="h-4 w-4 text-violet-600" /> Analyse IA</span>
                <button onClick={() => setAiOpen(false)} data-testid="wsi-mobile-ai-close"><X className="h-5 w-5 text-slate-500" /></button>
              </div>
              <AIPanel wsiId={id} demandeId={manifest.demande_id} canLaunch={canLaunchAI} mobile
                       onRegions={setAiRegions} onFocusRegion={focusRegion} />
            </div>
          )}
        </div>
        <div className="bg-slate-800 px-2 py-2 flex items-center gap-2 overflow-x-auto" data-testid="wsi-mobile-toolbar">
          <button onClick={() => zoom(1.3)} className="h-10 w-10 rounded-lg bg-slate-700 text-white flex items-center justify-center"><ZoomIn className="h-5 w-5" /></button>
          <button onClick={() => zoom(0.77)} className="h-10 w-10 rounded-lg bg-slate-700 text-white flex items-center justify-center"><ZoomOut className="h-5 w-5" /></button>
          <button onClick={home} className="h-10 w-10 rounded-lg bg-slate-700 text-white flex items-center justify-center"><Home className="h-5 w-5" /></button>
          <div className="w-px h-8 bg-slate-600" />
          {tools.map(toolBtn)}
          {canViewAI && (
            <button onClick={() => setAiOpen(true)} data-testid="wsi-mobile-ai"
                    className="h-10 px-3 rounded-lg bg-violet-600 text-white flex items-center gap-1.5 text-sm ml-auto">
              <Brain className="h-4 w-4" /> IA
            </button>
          )}
          {canSign && (
            <button onClick={() => setSigOpen(true)} data-testid="wsi-mobile-sign"
                    className="h-10 px-3 rounded-lg bg-teal-600 text-white flex items-center gap-1.5 text-sm">
              <PenLine className="h-4 w-4" /> Signer
            </button>
          )}
        </div>
        <SignatureDialog open={sigOpen} onOpenChange={setSigOpen} wsiId={id}
                         demandeId={manifest.demande_id} onSigned={loadSignatures} />
      </div>
    );
  }

  // ===================== DESKTOP =====================
  const selected = annotations.find((a) => a.id === selectedId);
  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem-3rem)]" data-testid="wsi-desktop">
      {/* Barre supérieure */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/wsi")} data-testid="wsi-back"
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="h-4 w-4" /> Lames
          </button>
          <div>
            <h1 className="font-head text-lg font-bold text-slate-900 leading-none">{manifest.nom}</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {manifest.width}×{manifest.height} px · {manifest.max_level + 1} niveaux
              {manifest.mpp ? ` · ${manifest.mpp} µm/px` : ""} {manifest.has_icc ? "· ICC" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => zoom(1.3)} title="Zoom avant (+)" className="wsi-ctrl" data-testid="wsi-zoom-in"><ZoomIn className="h-4 w-4" /></button>
          <button onClick={() => zoom(0.77)} title="Zoom arrière (-)" className="wsi-ctrl" data-testid="wsi-zoom-out"><ZoomOut className="h-4 w-4" /></button>
          <button onClick={home} title="Ajuster (0)" className="wsi-ctrl" data-testid="wsi-home"><Home className="h-4 w-4" /></button>
          <button onClick={rotate} title="Rotation (R)" className="wsi-ctrl"><RotateCw className="h-4 w-4" /></button>
          <button onClick={fullscreen} title="Plein écran (Maj+F)" className="wsi-ctrl"><Maximize className="h-4 w-4" /></button>
          <button onClick={() => setShowInfo((s) => !s)} title="Infos (I)" className="wsi-ctrl"><Info className="h-4 w-4" /></button>
          <button onClick={() => setShowAnnos((s) => !s)} title="Annotations (N)" className="wsi-ctrl"><Layers className="h-4 w-4" /></button>
          <button onClick={() => setShowHelp(true)} title="Raccourcis (?)" className="wsi-ctrl" data-testid="wsi-help"><HelpCircle className="h-4 w-4" /></button>
          {canSign && (
            <button onClick={() => setSigOpen(true)} data-testid="wsi-sign"
                    className="ml-1 h-9 px-3 rounded-lg bg-teal-600 text-white flex items-center gap-1.5 text-sm hover:bg-teal-700">
              <PenLine className="h-4 w-4" /> Signer
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 flex gap-3 min-h-0">
        {/* Barre latérale gauche — outils */}
        <div className="flex flex-col gap-2 bg-white border border-slate-200 rounded-xl p-2" data-testid="wsi-left-sidebar">
          <button onClick={() => setActiveTool(null)} title="Navigation (V)" data-testid="wsi-tool-none"
                  className={`flex items-center justify-center h-10 w-10 rounded-lg border ${
                    activeTool === null ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            <MousePointer2 className="h-5 w-5" />
          </button>
          <div className="h-px bg-slate-100 my-0.5" />
          {tools.map(toolBtn)}
          <div className="h-px bg-slate-100 my-0.5" />
          <div className="flex flex-col gap-1.5 items-center pt-1" data-testid="wsi-colors">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)} title="Couleur (C)"
                      className={`h-6 w-6 rounded-full border-2 ${color === c ? "border-slate-800 scale-110" : "border-white"}`}
                      style={{ background: c }} data-testid={`wsi-color-${c.slice(1)}`} />
            ))}
          </div>
        </div>

        {/* Zone visionneuse */}
        <div className="flex-1 rounded-xl overflow-hidden border border-slate-200 min-w-0 relative">
          <OSDCanvas manifest={manifest} wsiId={id} mobile={false} activeTool={activeTool} color={color}
                     annotations={visible ? annotations : []} selectedId={selectedId}
                     onSelect={setSelectedId} onCreate={createAnnotation} viewerRef={viewerRef}
                     aiRegions={aiRegions} />
          {activeTool === "polygone" && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-slate-900/80 text-white text-xs px-3 py-1.5 rounded-full">
              Cliquez pour ajouter des points · double-clic pour terminer
            </div>
          )}
        </div>

        {/* Barre latérale droite — IA, annotations & signatures */}
        {(showAnnos || showInfo || canViewAI) && (
          <div className="w-72 flex-shrink-0 flex flex-col gap-3 min-h-0 overflow-y-auto" data-testid="wsi-right-sidebar">
            {canViewAI && (
              <AIPanel wsiId={id} demandeId={manifest.demande_id} canLaunch={canLaunchAI}
                       onRegions={setAiRegions} onFocusRegion={focusRegion} />
            )}
            {showInfo && (
              <div className="bg-white border border-slate-200 rounded-xl p-3 text-sm">
                <div className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Palette className="h-4 w-4" /> Informations</div>
                <dl className="space-y-1 text-xs text-slate-600">
                  <div className="flex justify-between"><dt>Coloration</dt><dd className="font-medium">{manifest.coloration || "—"}</dd></div>
                  <div className="flex justify-between"><dt>Type</dt><dd className="font-medium">{manifest.type_examen || "—"}</dd></div>
                  <div className="flex justify-between"><dt>Ajoutée par</dt><dd className="font-medium truncate max-w-[9rem]">{manifest.auteur}</dd></div>
                  <div className="flex justify-between"><dt>Signatures</dt><dd className="font-medium">{signatures.length}</dd></div>
                </dl>
              </div>
            )}
            {showAnnos && (
              <div className="bg-white border border-slate-200 rounded-xl p-3 flex-1 min-h-0 flex flex-col">
                <div className="font-semibold text-slate-800 mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm"><Layers className="h-4 w-4" /> Annotations ({annotations.length})</span>
                  <button onClick={() => setVisible((s) => !s)} className="text-xs text-sky-600 hover:underline">
                    {visible ? "Masquer" : "Afficher"}
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto space-y-1.5" data-testid="wsi-annotations-list">
                  {annotations.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">Aucune annotation</p>}
                  {annotations.map((a) => (
                    <div key={a.id} onClick={() => setSelectedId(a.id)}
                         data-testid={`wsi-anno-item-${a.id}`}
                         className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-xs ${
                           a.id === selectedId ? "bg-sky-50 border border-sky-200" : "hover:bg-slate-50 border border-transparent"}`}>
                      <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ background: a.couleur }} />
                      <span className="flex-1 truncate">
                        {DESKTOP_TOOLS.find((t) => t.id === a.type)?.label || a.type}
                        {a.texte ? ` — ${a.texte}` : ""}
                        {a.mesure != null ? ` (${a.mesure}${manifest.mpp ? " µm" : " px"})` : ""}
                      </span>
                      <span className="text-[10px] text-slate-400 truncate max-w-[4rem]">{a.auteur_nom}</span>
                      {canAnnotate && (
                        <button onClick={(e) => { e.stopPropagation(); deleteAnnotation(a.id); }}
                                data-testid={`wsi-anno-delete-${a.id}`}
                                className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Aide raccourcis */}
      {showHelp && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6"
             onClick={() => setShowHelp(false)} data-testid="wsi-help-overlay">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-head text-lg font-bold text-slate-900 mb-4">Raccourcis clavier</h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {SHORTCUTS.map(([k, d]) => (
                <div key={k} className="flex justify-between">
                  <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono text-slate-700">{k}</kbd>
                  <span className="text-slate-600 text-xs">{d}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowHelp(false)} className="mt-5 w-full py-2 bg-slate-900 text-white rounded-lg text-sm">Fermer</button>
          </div>
        </div>
      )}

      <SignatureDialog open={sigOpen} onOpenChange={setSigOpen} wsiId={id}
                       demandeId={manifest.demande_id} onSigned={loadSignatures} />
    </div>
  );
}
